import { Component, OnInit } from "@angular/core";
import { SkillsService } from "../../../../auth/service/skills.service";
import { UtilsService } from "../../../../auth/service/utils.service";
import { ActivatedRoute, Router } from "@angular/router";
import { locale as italian } from "../../../../i18n/it";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthenticationService } from "../../../../auth/service";
import { ISkill, ISkillForm } from "app/auth/models/skills";
import { IDatatableConfig } from "app/auth/models/datatable";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";

@Component({
  selector: "app-skills-list",
  templateUrl: "./skills-list.component.html",
  styleUrls: ["./skills-list.component.scss"],
})
export class SkillsListComponent implements OnInit {
  public config: IDatatableConfig;
  public contentHeader: IContentHeader;
  public skills: ISkill[];
  public tableLimit: number = 10;
  public searchKeyword: string = "";
  public searchStatus: number = 2;
  public currentSkill: ISkill;
  public new_skill: string = "";
  public showModalError: boolean = false;

  constructor(
    public _skillsService: SkillsService,
    public _utilService: UtilsService,
    private _routerService: Router,
    public _modalService: NgbModal,
    public _authenticationService: AuthenticationService,
    private _activatedRoute: ActivatedRoute,
  ) {
    this.contentHeader = {
      headerTitle: italian.data.SKILLS,
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: italian.data.MENU.MANAGE,
            isLink: true,
            link: "/dashboard/platform-management",
          },
          {
            name: italian.data.SKILLS,
            isLink: false,
          },
        ],
      },
    };

    this._activatedRoute.queryParams.subscribe((params) => {
      if (params.enabled && Number(params.enabled) < 3) {
        this.searchStatus = params.enabled;
      }
    });
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("show_menu_skills_list")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }
    await this.loadSkills();
  }

  updateTableLimit(event: { target: { value: string } }) {
    this.tableLimit = Number(event.target.value);
  }

  async loadSkills() {
    this.skills = await this._skillsService.getList();
    const activeStr: string = await this._utilService.translate("ACTIVATED");
    const disableStr: string = await this._utilService.translate("APPROVED_PENDING");
    this.skills.map((item: ISkill) => {
      item.enabled_label = item.enabled === -1 ? disableStr : activeStr;
    });
  }

  skillsList() {
    let items: ISkill[] = this.skills || [];

    const keywords: string = this.searchKeyword.trim().toLowerCase();
    if (keywords.length > 0) {
      items = items.filter((item: ISkill) => item.name.toLowerCase().includes(keywords));
    }

    if (this.searchStatus < 2) {
      items = items.filter((item: ISkill) => Number(item.enabled) === Number(this.searchStatus));
    }

    return items;
  }

  async addSkill() {
    if (!this._authenticationService.hasCapability("create_skill")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }

    if (!this.new_skill || this.new_skill.length < 1) {
      this.showModalError = true;
      return false;
    }

    const data: ISkillForm = {
      name: this.new_skill,
      enabled: 1,
    };
    this._utilService.showLoading();
    await this._skillsService
      .create(data)
      .then(async () => {
        this._utilService.hideLoading();
        this.new_skill = "";
        this._modalService.dismissAll();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_UPLOAD_SKILL" });
        this.skills = await this._skillsService.getList(true);
        await this.loadSkills();
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_UPLOAD_SKILL" });
      });
  }

  async editSkill() {
    if (!this._authenticationService.hasCapability("edit_skill")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }
    if (!this.currentSkill.name || this.currentSkill.name.length < 1) {
      this.showModalError = true;
      return false;
    }
    const data: ISkillForm = {
      name: this.currentSkill.name,
    };
    this._utilService.showLoading();
    await this._skillsService
      .update(this.currentSkill.id, data)
      .then(() => {
        this._utilService.hideLoading();
        this._modalService.dismissAll();
        this.loadSkills();
        this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_SKILL" });
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_EDIT_SKILL" });
      });
  }

  async deleteSkill(currentSkill) {
    if (!this._authenticationService.hasCapability("delete_skill")) {
      await this._routerService.navigateByUrl("/dashboard/main");
      return false;
    }
    if (currentSkill.used_in_users > 0 && currentSkill.enabled === 1) {
      await this._utilService.showSWALError({ text: "USED_SKILL_TEXT" });
      return;
    }

    let deleteText: string = await this._utilService.translate("ALERT.CONFIRM_DELETE_TEXT");
    deleteText += await this._utilService.translate("ALERT.CONFIRM_DELETE_THIS_OBJECT");
    deleteText += currentSkill.name;

    await this._utilService
      .showSWALDeleteConfirmation({ text: deleteText })
      .then(async (result) => {
        if (result.value) {
          await this._skillsService.delete(currentSkill.id);
          await this._utilService.showPopupItemDeleted().then(async () => {
            await this.loadSkills();
          });
        } else if (result.isDismissed) {
          await this._utilService.showSWALError({
            title: "ABORTED_TITLE",
            text: "ABORTED_TEXT",
          });
        }
      })
      .catch(() => this._utilService.showSWALError({ text: "ERROR_REMOVE_SKILL" }));
  }

  public modalOpen(modalBasic, currenSkill: number) {
    this._modalService.open(modalBasic, {
      size: "sm",
      centered: true,
    });
    this.new_skill = "";
    this.currentSkill = JSON.parse(JSON.stringify(currenSkill));
    this.showModalError = false;
  }

  async approve(id: number) {
    await this._utilService.showLoading();
    await this._skillsService
      .approve(id)
      .then(async () => {
        await this.loadSkills().then(async () => {
          await this._utilService.hideLoading();
          await this._utilService.showSWALSuccess({ text: "SUCCESS_APPROVE_SKILL" });
        });
      })
      .catch(async () => {
        await this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_EDIT_SKILL" });
      });
  }

  async reject(id: number) {
    await this._utilService.showLoading();
    await this._skillsService
      .reject(id)
      .then(async () => {
        await this.loadSkills().then(async () => {
          await this._utilService.hideLoading();
          await this._utilService.showSWALSuccess({ text: "SUCCESS_REJECT_SKILL" });
        });
      })
      .catch(async () => {
        await this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_EDIT_SKILL" });
      });
  }
}
